import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCustomer } from '../services/api';
import useDialogFocus from '../hooks/useDialogFocus';
import ClientFields, { EMPTY_CLIENT, clientPayload, copyOfClient } from './ClientFields';

/**
 * « Nouveau client » over a rendez-vous, rapport, facture…: the full client
 * record, saved for good in the one list of clients, then handed back so the
 * form can select it. `entity` says which selector asked: "company" (client
 * facturation) or "customer" (client intervention).
 *
 * The record can start blank or as a copy of an existing client (`source`, or
 * picked from `customers` in « Partir de »): every field is taken over and
 * stays editable; the original is never touched.
 */
export default function NewClientDialog({ entity, initial, source, customers = [], onCreated, onClose }) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const [sourceId, setSourceId] = useState(source?.id || '');
  const [client, setClient] = useState(() => (
    source ? copyOfClient(source) : { ...EMPTY_CLIENT, ...initial }
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useDialogFocus(dialogRef, { onEscape: () => { if (!saving) onClose(); } });

  // After the hook has moved focus into the dialog: start typing the name —
  // over a copy, the copied name is selected, ready to be replaced.
  useEffect(() => {
    const field = dialogRef.current?.querySelector(`#new-${entity}-name`);
    field?.focus();
    if (source) field?.select();
  }, [entity]);

  // Lists the source too when the caller's list does not hold it yet.
  const choices = source && !customers.some((c) => c.id === source.id)
    ? [source, ...customers] : customers;
  const original = choices.find((c) => c.id === sourceId);

  const pickSource = (id) => {
    setSourceId(id);
    const picked = choices.find((c) => c.id === id);
    setClient(picked ? copyOfClient(picked) : { ...EMPTY_CLIENT, ...initial });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!client.name.trim()) { setError(t('clients.nameRequired')); return; }
    setSaving(true);
    setError('');
    try {
      const created = await createCustomer(clientPayload({ ...client, name: client.name.trim() }));
      onCreated(created);
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.generic'));
      setSaving(false);
    }
  };

  const titleId = `new-${entity}-title`;
  const sourceFieldId = `new-${entity}-source`;
  return (
    <div className="confirm-backdrop client-dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form ref={dialogRef} tabIndex={-1} className="confirm-dialog client-dialog" role="dialog"
        aria-modal="true" aria-labelledby={titleId} onSubmit={handleSubmit} noValidate>
        <h2 id={titleId} className="confirm-title">{t('clients.newTitle', { context: entity })}</h2>
        {choices.length > 0 && (
          <div className={original ? 'client-source client-source-on' : 'client-source'}>
            <label htmlFor={sourceFieldId}>{t('clients.startFrom')}</label>
            <select id={sourceFieldId} className="form-control" value={sourceId}
              onChange={(e) => pickSource(e.target.value)}>
              <option value="">{t('clients.startBlank')}</option>
              {choices.map((c) => (
                <option key={c.id} value={c.id}>{t('clients.startCopyOf', { name: c.name })}</option>
              ))}
            </select>
            {original && (
              <p className="client-source-note" role="status">
                {t('clients.copyNote', { name: original.name })}
              </p>
            )}
          </div>
        )}
        <div className="client-dialog-body">
          <ClientFields key={sourceId || 'blank'} value={client} onChange={setClient} idPrefix={`new-${entity}`} />
        </div>
        {error && <div className="ai-error" role="alert">{error}</div>}
        <div className="confirm-actions client-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('clients.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('clients.creating') : t('clients.create', { context: entity })}
          </button>
        </div>
      </form>
    </div>
  );
}
